import React, { useState, useEffect, useRef } from 'react';
import { size } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { bindActionCreators } from 'redux';
import { useParams, useHistory } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

import { useStickyHeader } from 'hooks/useStickyHeader';
import { Loading } from 'components/Loading';
import { LeadFilterCards } from 'components/FilterCards';
import ErrorModal from 'components/Modal/ConfirmationModal';
import { saveStorage, getStorage } from 'utils/cookies';

// import { LeadNotification } from './Leads.Notification';
import * as actions from 'containers/Leads/actions';
import { fetchLeadCustomPreferences } from 'apollo/leads.query';
import Search from 'components/Search';
import { LeadsActionButton } from './Leads.ActionButton';
import { ViewOption } from './Leads.ViewOption';
import LeadLanding from './Leads.Landing';
import LeadsTable from './Leads.Table';
import './styles.scss';

dayjs.extend(relativeTime);

const ITEMS_PER_PAGE = [10, 25, 50, 100];
const SORTABLE_COLUMNS = [
  {
    id: 'contact_first_name',
    label: 'Lead Contact',
  },
  {
    id: 'wedding_date',
    label: 'Event Date/Location',
  },
  {
    id: 'wedding_date',
    label: 'Event Date',
  },
  {
    id: 'date_inquiry',
    label: 'Inquiry Date',
  },
  {
    id: 'status',
    label: 'Status',
  },
  {
    id: 'last_contacted_date',
    label: 'Last Contacted',
  },
  {
    id: 'project_name',
    label: 'Project Name',
  },
  {
    id: 'service_type',
    label: 'Service Type',
  },
  {
    id: 'budget',
    label: 'Budget',
  },
  {
    id: 'lead_source',
    label: 'Lead Source',
  },
];
const SEARCH_OPTIONS = [
  {
    id: 'contact_name',
    label: 'Full Name',
    checked: true,
  },
  {
    id: 'company_name',
    label: 'Company Name',
    checked: true,
  },
  /* Hide for now until project name is sorted out better.
  {
    id: 'project_name',
    label: 'Project Name',
    checked: true,
  },
  */
  {
    id: 'primary_email',
    label: 'Email Address',
    checked: true,
  },
];

const Leads = ({ leadsCount }) => {
  const history = useHistory();
  const { status: statusId, organization: orgId } = useParams();

  // global store
  const filters = useSelector(state => state.leads.filters);
  const organization = useSelector((state) => state.global.userInfo.organization);
  const username = useSelector((state) => state.global.userInfo.username);
  const { datePreference } = organization;

  // leads store
  const leadViewOptions = useSelector((state) => state.global.leadsViewOptions);
  const firstLoadComplete = useSelector(state => state.leads.firstLoadComplete);
  const allData = useSelector(state => state.leads.leads);
  const leadStatusError = useSelector(state => state.leads.error);
  const dispatch = useDispatch();
  const {
    getLeads: fetchLeads,
    getLeadsFiltersInBackground,
    searchLeads,
    updateLeadCancel,
  } = bindActionCreators(actions, dispatch);

  // filtered status
  const [selectedStatus, setCurrentLeadStatus] = useState(statusId ?? 'current');
  const [filterByAssignment, setFilterByAssignment] = useState(null);
  const prevStatus = useRef();

  // Search By
  const [searchKey, setSearchValue] = useState('');
  const [searchOptions, setSearchOptionList] = useState(SEARCH_OPTIONS);

  // Sort by
  const [sortColumn, changeSortColumn] = useState({ sortReverse: 0, name: 'Inquiry Date' });
  const sortColumnId = SORTABLE_COLUMNS.filter(el => el.label === sortColumn.name)[0]?.id;
  const sortColumnReverse = sortColumn.sortReverse;

  // Pagination
  const [pageNum, setPageNumber] = useState(0);
  const [countPerPage, setCountPerPage] = useState(ITEMS_PER_PAGE[0]);

  // Leads custom preferences
  const [lostReasons, setLostReasons] = useState(null);

  // Determine if username is loaded to reduce number of calls in initial load.
  const [loaded, setLoaded] = useState(firstLoadComplete);

  // Make filter cards sticky after scrolling past.
  const { elementRef, isSticky } = useStickyHeader();

  // Scroll anchor for top of table, offset with css because of sticky nav.
  const tableTopPos = document.getElementById('scroll-anchor')?.getBoundingClientRect();

  useEffect(() => {
    localStorage.setItem('leadroute', selectedStatus);
  }, [selectedStatus]);

  useEffect(() => {
    const fetchCustomPrefs = async () => {
      const items = await fetchLeadCustomPreferences();
      if (items && items.data) {
        const { leadCustomPreferences } = items.data;
        setLostReasons(leadCustomPreferences.filter(pref => {
          return pref.kind === 'reason-for-not-booking' && pref.visibility;
        }));
      }
    };
    fetchCustomPrefs();
  }, []);

  useEffect(() => {
    // Username isn't set initially, so need to wait to be able to
    // retrieve cookie and determine if first leadsByStatus fetch
    // should use non-default values for pageNum, countPerPage, and assignment filter.
    if (username) {
      const leadsCookie = getStorage('lead-cookie', username);
      const cookiePageNum = leadsCookie?.pageNum;
      if (!Number.isInteger(cookiePageNum)) {
        setPageNumber(0);
      } else {
        setPageNumber(cookiePageNum || 0);
      }
      setCountPerPage(leadsCookie?.pageSize || ITEMS_PER_PAGE[0]);
      if (leadsCookie?.filterByAssignment) {
        const filterBy = leadsCookie?.filterByAssignment;
        setFilterByAssignment(filterBy);
        getLeadsFiltersInBackground({ variables: { id: filterBy[0] } });
      }
    }
    setLoaded(true);
  }, [username]);

  const setStatus = (status) => {
    setPageNumber(0);
    history.push(`/app/v2/${orgId}/leads/status/${status}`);
    setCurrentLeadStatus(status);
  };

  const fetchTableData = () => {
    let variables = {
      statusId: String(selectedStatus === 'undefined' ? 'current' : selectedStatus ?? 'current'),
      page: pageNum,
      pageSize: countPerPage,
    };

    if (sortColumnReverse >= 0) {
      variables = {
        ...variables,
        orderBy: sortColumnId,
        sortReverse: sortColumnReverse === 0,
      };
    }
    if (filterByAssignment) {
      variables = {
        ...variables,
        filterAssignments: filterByAssignment,
      };
    }
    fetchLeads({ variables });
  };

  const searchTableData = () => {
    const params = {
      text: [],
      email: [],
    };
    searchOptions.forEach((opt) => {
      if (!opt.checked) return;
      if (opt.id === 'contact_name') {
        params.text.push('contact_first_name');
        params.text.push('contact_last_name');
      } else if (opt.id === 'primary_email') {
        params.email.push('primary_email');
      } else {
        params.text.push(opt.id);
      }
    });

    searchLeads({
      variables: {
        searchKey,
        params,
        page: pageNum,
        pageSize: countPerPage,
      },
    });
  };

  const cancelErrorModal = () => {
    updateLeadCancel();
  };

  useEffect(() => {
    if (filters?.length) {
      if (statusId === 'current' || statusId === 'all') return;
      let changedStatus = 'current';
      if (filters.findIndex(fl => fl.id === Number(statusId)) > -1) {
        changedStatus = statusId;
      }
      history.push(`/app/v2/${orgId}/leads/status/${changedStatus}`);
    }
  }, [filters, statusId]);

  useEffect(() => {
    if (searchKey && selectedStatus) setPageNumber(0);
  }, [searchKey, selectedStatus]);

  useEffect(() => {
    // Wait to see if there is user data in leads
    // cookie first before attempting first fetch.
    if (loaded) {
      if (!searchKey) {
        fetchTableData();
      } else {
        searchTableData();
      }
    }
  }, [searchKey, selectedStatus, sortColumnId, sortColumnReverse,
    pageNum, countPerPage, filters, searchOptions, loaded]);

  useEffect(() => {
    if (username && loaded) {
      saveStorage('lead-cookie', username, {
        pageNum,
        pageSize: countPerPage,
        filterByAssignment,
      });
    }
  }, [pageNum, countPerPage, filterByAssignment]);

  const changeFilterAssignment = (ids) => {
    // Refetch filters numbers when filtering by assignment.
    if (ids?.length > 0) {
      getLeadsFiltersInBackground({ variables: { id: ids[0] } });
    }
    else {
      getLeadsFiltersInBackground();
    }

    setFilterByAssignment(ids);
  };

  // Scroll to top of table when changing pages,
  // but not on initial load.
  const scrollToTop = (anchor = false) => {
    if (anchor) {
      const scrollTo = tableTopPos?.top + window.scrollY;
      // Need to use scrollTo() because scrollIntoView wasn't completing
      // when the table re-renders for some reason.
      if (loaded) window.scrollTo({ top: scrollTo, behavior: 'smooth' });
    }
    else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  useEffect(() => {
    if (prevStatus?.current === selectedStatus) {
      scrollToTop(true);
    } else {
      scrollToTop();
    }
    prevStatus.current = selectedStatus;
  }, [pageNum, selectedStatus]);

  const resetSearch = () => {
    setSearchValue('');
  };

  const renderEmptyLeads = () => (
    <div className="leads-index__empty">There are currently no leads with this status.</div>
  );

  const renderLoading = () => (
    <Loading />
  );

  const getLeadReponse = (items) => {
    if (!items) return [];
    return items?.map(aLead => {
      const { dateInquiry, eventDate, lastContactedDate, ...rest } = aLead;
      const dateFormat = datePreference?.toUpperCase();
      return {
        dateInquiry: dateInquiry ? dayjs(dateInquiry).format('MM/DD/YYYY') : '',
        eventDate: eventDate ? dayjs(eventDate).format(dateFormat) : '',
        eventDate1: eventDate ? dayjs(eventDate) : '',
        lastContactedDate: lastContactedDate ? dayjs(lastContactedDate).format(dateFormat) : '',
        lastContactedDate1: lastContactedDate ? dayjs(lastContactedDate) : '',
        ...rest,
      };
    });
  };

  const leads = getLeadReponse(allData?.items);
  const nTotalCount = allData?.total || 0;

  if (!leadsCount) {
    return (
      <div className="content-container content-container--narrow index-page leads-index">
        <LeadLanding statuses={filters} />
      </div>
    );
  }

  return (
    <div className="content-container content-container--fluid index-page leads-index">
      <section className="page-title page-title--actions">
        <h1 className="title">Leads</h1>
        <Search
          placeholderText="Search Leads"
          fetchTableData={fetchTableData}
          searchTableData={searchTableData}
          options={SEARCH_OPTIONS}
          searchKey={searchKey}
          setSearchValue={setSearchValue}
          searchOptions={searchOptions}
          setSearchOptionList={setSearchOptionList}
        />
        <div className="page-title__actions">
          <LeadsActionButton
            statuses={filters}
            isEmpty={!size(leads)}
          />
        </div>
      </section>
      <div key="leads-table-index">
        <div className="table_status_selector">
          <LeadFilterCards
            // Refresh hooks when statuses are added or removed.
            key={`leads-filter-cards--${filters.length}`}
            ref={elementRef}
            filters={filters}
            selected={selectedStatus}
            setFilter={setStatus}
            isSearching={!!searchKey}
            resetSearch={resetSearch}
            isSticky={isSticky}
          />
        </div>
        <div className="index-page__top leads-index__top">
          <ViewOption
            options={leadViewOptions}
            selected={selectedStatus}
            sortColumn={sortColumn}
            changeSortColumn={changeSortColumn}
            filterAssignment={filterByAssignment}
            changeFilterAssignment={changeFilterAssignment}
          />
          <div
            id="scroll-anchor"
            className="leads-pager-scroll-anchor"
          />
        </div>
        {(!loaded || !firstLoadComplete) ? renderLoading() :
          !size(leads) ? renderEmptyLeads() : (
            <LeadsTable
              leads={leads}
              lostReasons={lostReasons}
              selected={selectedStatus}
              totalCount={nTotalCount}
              pageNum={pageNum}
              changePage={setPageNumber}
              pageSize={countPerPage}
              changePageSize={setCountPerPage}
              sortColumn={sortColumn}
              changeSortColumn={changeSortColumn}
            />
        )}
      </div>
      {leadStatusError && (
        <ErrorModal
          visible={!!leadStatusError}
          onClose={cancelErrorModal}
          title="Oops!"
          actions={(
            <button
              onClick={cancelErrorModal}
              className="button"
            >
              Close
            </button>
          )}
        >
          <p>An error occured. Please try again or contact support.</p>
        </ErrorModal>
      )}
    </div>
  );
};

export default Leads;
